<template>
  <div class="container section">
    <div class="section__title-container">
      <div class="section__title-left">
        <div class="section__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="section__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="section__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="section__title-right"
           v-if="isDataFetched">
        <div class="section__sections">
          <span class="section__count">Sections: {{totalItems}}</span>
          <base-select
            class="section__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="section__button" @click="openCreateModal()">
          Create Section
        </button>
      </div>
    </div>

    <div class="common-table section__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in sectionTableColumnsConfig"
             :key="column.property"
             :class="[`section__${column.property}`]">
          <base-multi-select-sortable-header
            v-if="column.property !== 'edit-button'"
            class="section__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedNumberFilter="['urls'].includes(column.property) ? filterParams[column.property] : null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="section__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="section in paginatedSections">
          <div :key="section.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in sectionTableColumnsConfig"
                 :key="column.property"
                 :class="[`section__${column.property}`, `section__row_${column.property}`]">
              <template v-if="column.property === 'edit-button'">
                <a @click="openCreateModal(section.id)" class="edit-icon">
                  <base-material-design-icon icon="edit"/>
                </a>
              </template>
              <template v-else-if="column.property === 'is_active'">
                <base-select class="section__select"
                             :options="activeOptions"
                             borderLess
                             alignLeft
                             shiftLabelRight
                             :selectStyle="{height: '60px!important', width: '75px', marginLeft: '-10px'}"
                             v-model="section.is_active"
                             @change="createOrUpdateSection(section)"/>
              </template>
              <template v-else-if="column.property === 'name'">
                <base-truncate-label class="section__truncate"
                                     :label="section[column.property]"
                                     watchClass="section__truncate"/>
              </template>
              <template v-else>
                  <span>
                    {{ section[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="section__update-loader" v-if="isDataUpdating">
      <base-loader class="section__update-loader-ring"/>
    </div>

    <modal name="create"
           v-bind="changeSectionModalProps">
      <CreateUpdateSectionPopup
        ref="create-section"
        @apply="createOrUpdateSection($event)"
        @delete="deleteSection($event)"
        :selected-section="selectedSection"
        :data="sectionData"
        @cancel="closeCreateModal"/>
    </modal>

  </div>
</template>

<script>
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateUpdateSectionPopup from './CreateUpdateSectionPopup'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'

const ITEMS_PER_PAGE = 100
const EMPTY_FILTERS = {
  name: '',
  is_active: [],
  urls: {
    operator: null,
    value: null
  },
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'edit-button'
  },
  {
    label: 'Name',
    property: 'name'
  },
  {
    label: 'Active',
    property: 'is_active',
    isSmartInput: false,
    options: [
      {
        label: 'Yes',
        value: 1
      },
      {
        label: 'No',
        value: -1
      }
    ]
  },
  {
    label: 'URLs',
    property: 'urls',
    isNumber: true
  }
]
const ACTIVE_OPTIONS = [
  {
    label: 'Yes',
    value: 1
  },
  {
    label: 'No',
    value: 0
  }
]
const PAGINATION_OPTIONS = [
  {
    label: '100',
    value: 100
  },
  {
    label: '250',
    value: 250
  },
  {
    label: '500',
    value: 500
  }
]
export default {
  name: 'Sections',
  components: { CreateUpdateSectionPopup },
  mixins: [BaseSortMixins],
  data () {
    return {
      activeOptions: _.cloneDeep(ACTIVE_OPTIONS),
      isDataFetched: false,
      selectedSection: null,
      isDataUpdating: false,
      filteredSections: [],
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      sectionTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'name': 'name',
        'is_active': 'is_active',
        'urls': 'urls'
      },
      paginationOptions: _.cloneDeep(PAGINATION_OPTIONS)
    }
  },
  computed: {
    ...mapState('sections', ['sections']),
    ...mapState('projectSettings', {
      sectionData (state) {
        return {
          opts: {
            is_active: [{ value: 1, label: 'Yes' }, { value: 0, label: 'No' }],
            target_geo: [{ value: -1, label: '--- Not Set ---' }, ...state.targetGeoOptions.sort((a, b) => (a.label > b.label) ? 1 : -1)],
            target_device: [{ value: -1, label: '--- Not Set ---' }, ...state.targetDeviceOptions.sort((a, b) => (a.label > b.label) ? 1 : -1)],
            target_locale: [{ value: -1, label: '--- Not Set ---' }, ...state.targetLocaleOptions.sort((a, b) => (a.label > b.label) ? 1 : -1)]
          },
          defaultSection: state.projectData
        }
      }
    }),
    dataMissing () {
      return this.isDataFetched && !this.sections
    },
    changeSectionModalProps: function () {
      const changeSectionModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeSectionModalProps }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    paginatedSections () {
      let sections = _.cloneDeep(this.sections)
      // eslint-disable-next-line camelcase
      const { page_from = 0, page_to = 100 } = this.filterParams
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = sections.length
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.filteredSections = sections
      return sections.slice(page_from, page_to)
    }
  },
  watch: {
    clientId: {
      handler (v) {
        v && this.loadData()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('sections', ['getSectionData', 'createSection', 'deleteSectionById', 'updateSection']),
    ...mapActions('projectSettings', ['getProjectSettings']),
    async loadData () {
      this.isDataFetched && (this.isDataUpdating = true)
      const filters = _.cloneDeep(this.filterParams)
      if (filters.is_active.includes(-1)) {
        filters.is_active.push(0)
      }
      await Promise.all([
        this.getSectionData({
          clientId: this.clientId,
          filters
        }),
        this.getProjectSettings(this.clientId)
      ])
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    async createOrUpdateSection (section) {
      this.isDataUpdating = true
      try {
        if (section.id && section.id > -1) {
          await this.updateSection({ clientId: this.clientId, section })
        } else {
          await this.createSection({ clientId: this.clientId, section })
        }
        await this.loadData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-section'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    async deleteSection (sectionId) {
      this.isDataUpdating = true
      try {
        await this.deleteSectionById({ clientId: this.clientId, sectionId })
        await this.loadData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-section'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    closeCreateModal () {
      this.selectedSection = null
      this.$modal.hide('create')
    },
    openCreateModal (sectionId) {
      this.selectedSection = this.sections.find(section => section.id === sectionId)
      this.$modal.show('create')
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadData()
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
      !this.isDataUpdating && this.loadData()
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
      this.loadData()
    }
  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: $rfx-default-border-radius;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }

    &:last-child {
      padding-right: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.section {
  position: relative;
  min-height: 200vh !important;

  &__row {
    &_name {
      max-width: 31.5%;
    }
  }

  &__truncate {
    width: 90%;
  }

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .section__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__sections {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__urls {
    &.common-table__header-cell {
      flex: 0 0 34%;
    }
  }

  &__edit-button {
    flex: 0 0 45px;

    &.common-table__header-cell {
      flex-basis: 0;
    }
  }

  &__name {

    &.common-table__header-cell {
      flex-basis: 3.3%;

      &::v-deep .multi-select__title-container {
        padding-left: 68px;
      }
    }
  }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;

    &__asana {
      margin: 15px 0;
    }
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    width: 75px;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: $rfx-default-border-radius;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.common-table {
  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
  }
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

</style>
